import {Component, OnDestroy, OnInit, ViewChild, ViewContainerRef} from '@angular/core';
import {MatDialog, MatDialogConfig, MatDialogRef} from '@angular/material/dialog';
import {MatSidenav} from '@angular/material/sidenav';
import {AppStore} from '../store/store';
import {SegmentCreateComponent, SegmentCreationData} from './info/create/create.component';
import {SegmentDeleteComponent} from './info/delete/delete.component';
import {SegmentListComponent} from './list/list.component';
import {SubSink} from 'subsink';
import {ofType} from '@ngrx/effects';
import {closeSegmentDetails, closeSegmentDetailsAndClearSelection, loadSegmentDetails} from './segments.state';
import {ActionsSubject} from '@ngrx/store';
import {loadTargets} from '../target/target.state';
import {SegmentState} from './segment.model';
import {
  ActiveSegmentListConfig,
  DeletedSegmentListConfig,
  HierarchicalActiveSegmentListConfig
} from './segment-config.model';
import {ApiVersion} from '../app.types';

@Component({
  selector: 'app-segments',
  template: `
    <mat-sidenav-container>

      <mat-sidenav #info mode="side" position="end">
        <ng-container [ngSwitch]="activeSegmentState">
          <app-segment-info *ngSwitchCase="segmentState.ACTIVE" data-cy="component-segment-info"
                            [config]="getActiveSegmentListConfig()"
                            [apiVersion]="selectedApiVersion"
                            (showDeleteSegmentDialog)="showDeleteSegmentDialog()"></app-segment-info>
          <app-segment-info *ngSwitchCase="segmentState.DELETED" data-cy="component-segment-info"
                            [config]="deletedSegmentListConfig"
                            [apiVersion]="selectedApiVersion"
                            (showDeleteSegmentDialog)="showDeleteSegmentDialog()"></app-segment-info>
        </ng-container>
      </mat-sidenav>

      <mat-sidenav-content>
        <div style="text-align: center;">
          <mat-button-toggle-group style="margin: 10px" name="fontStyle" aria-label="Font Style"
                                   [value]="activeSegmentState"
                                   (change)="changeState()">
            <mat-button-toggle *ngFor="let state of segmentStateKeys" [value]="state"
                               [matTooltip]="'Displays '+state.toLowerCase()+' segments'"
                               [attr.data-cy]="'segments-state-'+state.toLowerCase()">{{state | titlecase}}</mat-button-toggle>
          </mat-button-toggle-group>
          <mat-button-toggle-group style="margin: 10px" [value]="selectedApiVersion"
                                   (change)="changeApiVersion($event.value)">
            <mat-button-toggle *ngFor="let apiVersion of apiVersionValues; let index = index;" [value]="apiVersion"
                               [attr.data-cy]="'segments-api-'+index"
                               [matTooltip]="'Displays segments in different modes, flat = standard segments view, hierarchical ' +
                                '= includes relations between segments in a tree-like structure'">
              {{apiVersionDisplayValue(apiVersion)}}
            </mat-button-toggle>
          </mat-button-toggle-group>
        </div>

        <ng-container [ngSwitch]="activeSegmentState">
          <app-segment-list #activeSegmentList *ngSwitchCase="segmentState.ACTIVE"
                            [config]="getActiveSegmentListConfig()"
                            [apiVersion]="selectedApiVersion"
                            data-cy="component-segment-list"
                            (showCreateSegmentDialog)="showCreateSegmentDialog()"></app-segment-list>
          <app-segment-list #deletedSegmentList *ngSwitchCase="segmentState.DELETED" [config]="deletedSegmentListConfig"
                            [apiVersion]="selectedApiVersion"
                            data-cy="component-segment-list"
                            (showCreateSegmentDialog)="showCreateSegmentDialog()"></app-segment-list>
        </ng-container>
      </mat-sidenav-content>

    </mat-sidenav-container>

  `,
  styleUrls: ['segment.component.scss']
})
export class SegmentComponent implements OnInit, OnDestroy {
  segmentState = SegmentState;
  segmentStateKeys = Object.keys(SegmentState);
  activeSegmentState: SegmentState = SegmentState.ACTIVE;
  apiVersion: typeof ApiVersion = ApiVersion;
  apiVersionValues = [ApiVersion.V1, ApiVersion.V2];
  selectedApiVersion: ApiVersion = ApiVersion.V1;
  deletedSegmentListConfig = DeletedSegmentListConfig;
  @ViewChild('info') segmentInfo!: MatSidenav;
  @ViewChild('activeSegmentList') activeSegmentListComponent!: SegmentListComponent;
  @ViewChild('deletedSegmentList') deletedSegmentListComponent!: SegmentListComponent;
  deleteDialogRef!: MatDialogRef<SegmentDeleteComponent>;
  createDialogRef!: MatDialogRef<SegmentCreateComponent>;
  subsink = new SubSink();
  private config!: MatDialogConfig;

  constructor(private dialog: MatDialog,
              private viewContainerRef: ViewContainerRef,
              private appStore: AppStore,
              private actions: ActionsSubject) {
  }

  getActiveSegmentListConfig() {
    if (this.selectedApiVersion === ApiVersion.V1) {
      return ActiveSegmentListConfig;
    }
    return HierarchicalActiveSegmentListConfig;
  }

  ngOnInit() {
    this.appStore.dispatch(loadTargets());
    this.reactToSegmentDetailsActions();
    this.config = new MatDialogConfig();
    this.config.viewContainerRef = this.viewContainerRef;
  }

  showCreateSegmentDialog() {
    this.createDialogRef = this.dialog.open(SegmentCreateComponent, {
      ...this.config,
      data: <SegmentCreationData>{apiVersion: this.selectedApiVersion}
    });
  }

  showDeleteSegmentDialog() {
    this.deleteDialogRef = this.dialog.open(SegmentDeleteComponent, this.config);
  }

  ngOnDestroy(): void {
    this.subsink.unsubscribe();
  }

  changeState() {
    this.activeSegmentState = this.activeSegmentState === SegmentState.ACTIVE ? SegmentState.DELETED : SegmentState.ACTIVE;
    this.closeSegmentInfo();
  }

  changeApiVersion(value: ApiVersion) {
    this.selectedApiVersion = value;
    this.closeSegmentInfo();
  }

  apiVersionDisplayValue(value: string) {
    switch (value) {
      case ApiVersion.V1:
        return 'Flat';
      case ApiVersion.V2:
        return 'Hierarchical';
      default:
        return 'Should not happen!';
    }
  }

  private reactToSegmentDetailsActions() {
    this.subsink.sink = this.actions.pipe(
      ofType(closeSegmentDetails, closeSegmentDetailsAndClearSelection)
    ).subscribe(ev => {
      if (ev.type === closeSegmentDetailsAndClearSelection.type) {
        if (this.activeSegmentState === SegmentState.ACTIVE) {
          this.activeSegmentListComponent.clearSelection();
        } else {
          this.deletedSegmentListComponent.clearSelection();
        }
      }
      this.closeSegmentInfo();
    });

    this.subsink.sink = this.actions.pipe(
      ofType(loadSegmentDetails)
    ).subscribe(_ => {
      this.openSegmentInfo();
    });
  }

  private closeSegmentInfo() {
    this.segmentInfo?.close()?.then();
  }

  private openSegmentInfo() {
    if (this.segmentInfo && !this.segmentInfo.opened) {
      this.segmentInfo.open()?.then(() => {
      });
    }
  }
}
